/**
 * OpenAI Realtime model registry.
 *
 * Single source of truth for every selectable Realtime model: identifier,
 * display name, context window, per-token pricing, supported voices, and
 * capability flags. Backend adapters and frontend pickers read from here —
 * no hardcoded model strings anywhere else.
 *
 * Pricing is recorded per model, per modality and per cache state
 * ({@link RealtimeTokenPricing}), so a new model needs zero changes to the
 * billing adapter — only an entry in this map.
 *
 * A model joins this table only when the adapter needs no new work to run it:
 * the same session payload, the same events, and a price the
 * {@link RealtimeTokenPricing} shape can state exactly.
 *
 * @see https://openai.com/index/advancing-voice-intelligence-with-new-models-in-the-api/
 * @see https://developers.openai.com/api/docs/pricing
 */

import { z } from "zod";

import { type RealtimeTokenPricing } from "./RealtimeTokenPricing.schema";

// ============================================================
// Voices (superset across all models — capability-gated below)
// ============================================================

/**
 * All voices ever shipped by the OpenAI Realtime API, in release order.
 * Per-model availability lives on each `OpenAIRealtimeModel.voices` entry.
 */
export const OPENAI_REALTIME_VOICES = [
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse",
  // gpt-realtime-2 additions:
  "cedar",
  "marin"
] as const;
export type OpenAIRealtimeVoice = (typeof OPENAI_REALTIME_VOICES)[number];

/** Voices available on the legacy gpt-realtime-1.5 model. */
const V1_5_VOICES: readonly OpenAIRealtimeVoice[] = [
  "alloy",
  "ash",
  "ballad",
  "coral",
  "echo",
  "sage",
  "shimmer",
  "verse"
];

/** Voices available on gpt-realtime-2 — superset of V1_5_VOICES. */
const V2_VOICES: readonly OpenAIRealtimeVoice[] = [...V1_5_VOICES, "cedar", "marin"];

// ============================================================
// Model registry
// ============================================================

export const OPENAI_REALTIME_MODEL_IDS = [
  "gpt-realtime-2.1",
  "gpt-realtime-2.1-mini",
  "gpt-realtime-2",
  "gpt-realtime-1.5"
] as const;
export type OpenAIRealtimeModelId = (typeof OPENAI_REALTIME_MODEL_IDS)[number];
export const OpenAIRealtimeModelIdSchema = z.enum(OPENAI_REALTIME_MODEL_IDS);

/**
 * Capability flags. Adapters check these before emitting model-specific
 * session config (e.g. preambles only sent when supported). Add a flag
 * here when — and only when — at least one adapter consumes it; dead
 * capability bits silently rot until someone trips over them.
 */
export interface OpenAIRealtimeCapabilities {
  /** Model supports `preambles` — short narration phrases before tool calls. */
  preambles: boolean;
  /**
   * Model supports `reasoning.effort` configuration. Emitted as
   * `session.reasoning.effort` on `session.update` and echoed back on
   * `session.created` / `session.updated`. Per-turn overrides are also
   * accepted via `response.create.response.reasoning.effort`. Only the
   * reasoning-capable Realtime models (v2+) honour the field; older models
   * reject it and silent-drop the whole `session.update` payload, so the
   * gate is mandatory.
   */
  reasoningEffort: boolean;
  /**
   * Model honours `max_output_tokens` (number | "inf") at session level.
   * The field was renamed from `max_response_output_tokens` on the beta
   * surface to `max_output_tokens` on GA — the session.created echo body
   * confirms the GA name. Older models ignore the field; gating prevents
   * emitting it where it has no effect (and clutters the session payload).
   * Capability key is kept as `maxResponseOutputTokens` for stored
   * deployment-settings backwards compatibility.
   */
  maxResponseOutputTokens: boolean;
  /**
   * Model supports overriding the user-transcript model via
   * `input_audio_transcription.model`. v2+ ships with `gpt-realtime-whisper`
   * which produces noticeably better French/Arabic transcripts than
   * `whisper-1`; legacy models stay on whisper-1 to avoid surprise.
   */
  transcriptModel: boolean;
  /**
   * Model supports the `wait_for_user` no-op tool injection. The tool gives
   * the model a valid non-speaking action when it hears silence, hold music,
   * or background noise — replacing the default behaviour of producing
   * filler phrases like "I'm here" or "I didn't catch that".
   *
   * @see https://platform.openai.com/docs/guides/realtime-voice-agents
   */
  waitForUser: boolean;
  /**
   * Model supports `audio.output.speed` — playback rate multiplier for the
   * synthesised speech. Range is 0.25–1.5. Older models predate the nested
   * `audio.*` namespace and will reject the field with `invalid_request`,
   * so gating is mandatory.
   */
  audioOutputSpeed: boolean;
  /**
   * Model supports `tracing` in session config — opts the session into
   * OpenAI's own tracing dashboard so their support engineers can debug
   * failed calls server-side. The field shipped with the GA release
   * (`gpt-realtime-2`); pre-GA models reject it with `invalid_request`.
   * Independent of our own LLMTracingService — both can run in parallel.
   */
  openAITracing: boolean;
}

export interface OpenAIRealtimeModel {
  id: OpenAIRealtimeModelId;
  /** Human-readable name for UI display. */
  displayName: string;
  /** Total context window in tokens. */
  contextWindowTokens: number;
  /** Max chars for the `instructions` field — split-vs-inline threshold. */
  maxInstructionsChars: number;
  /** Default sampling temperature for this model. */
  temperature: number;
  /** Default voice when none is configured. */
  defaultVoice: OpenAIRealtimeVoice;
  voices: readonly OpenAIRealtimeVoice[];
  /**
   * Voices that OpenAI explicitly recommends as best-quality for this model.
   * Surfaced in the picker as a "Best quality" badge so customers default to
   * the strongest option without having to read provider release notes.
   */
  recommendedVoices: readonly OpenAIRealtimeVoice[];
  /**
   * Default user-transcript model — sent as `input_audio_transcription.model`
   * unless overridden by `OpenAIRealtimeFeatureSettings.transcriptModel`.
   * Only consulted when `capabilities.transcriptModel` is true.
   */
  defaultTranscriptModel: OpenAIRealtimeTranscriptModel;
  pricing: RealtimeTokenPricing;
  capabilities: OpenAIRealtimeCapabilities;
}

// ============================================================
// Transcript models
// ============================================================

/**
 * Models the Realtime API accepts for `input_audio_transcription.model`.
 * `gpt-realtime-whisper` ships with v2 and is materially better on
 * non-English languages; `whisper-1` is the legacy default and the only
 * choice on v1.5.
 */
export const OPENAI_REALTIME_TRANSCRIPT_MODELS = ["gpt-realtime-whisper", "whisper-1"] as const;
export type OpenAIRealtimeTranscriptModel = (typeof OPENAI_REALTIME_TRANSCRIPT_MODELS)[number];
export const OpenAIRealtimeTranscriptModelSchema = z.enum(OPENAI_REALTIME_TRANSCRIPT_MODELS);

/**
 * Per-transcript-model capability flags.
 *
 * The realtime model and the transcript model are independent fields on the
 * Realtime session payload — a `gpt-realtime-2` deployment may opt back to
 * `whisper-1`, and a hypothetical future v1.5 deployment could opt into
 * `gpt-realtime-whisper`. Constraints on `input_audio_transcription.*`
 * therefore live with the transcript model, NOT the realtime model.
 *
 * Adding a new transcript model: extend the union above, then add an entry
 * here. Forgetting the capability entry is a compile-time error — the
 * `Record<OpenAIRealtimeTranscriptModel, ...>` shape makes it impossible to
 * register a transcript model without declaring its capabilities.
 */
export interface OpenAITranscriptModelCapabilities {
  /**
   * Whether the transcript model accepts `input_audio_transcription.prompt`.
   * GA-era Realtime rejects the field on `gpt-realtime-whisper` with
   * `invalid_value` → the whole `session.update` is silently dropped on the
   * wire and the adapter's hard-fail guard tears the session down. Gating
   * emission on this capability keeps the field useful for `whisper-1`
   * while preventing the silent-degradation failure mode on
   * `gpt-realtime-whisper`.
   */
  prompt: boolean;
}

export const OPENAI_TRANSCRIPT_MODEL_CAPABILITIES: Record<
  OpenAIRealtimeTranscriptModel,
  OpenAITranscriptModelCapabilities
> = {
  "whisper-1": { prompt: true },
  "gpt-realtime-whisper": { prompt: false }
};

/**
 * Whether a non-empty `transcriptPrompt` would be silently ignored at the
 * wire boundary for the given transcript model. Centralises the predicate
 * used by:
 *   - the backend session-config builder (to gate emission of the field)
 *   - the backend session-config builder (to emit an ops warning when set
 *     but ignored, so the silent omission is not invisible in logs)
 *   - the frontend deployment form (to surface an inline hint to the user)
 *
 * Returns `false` for `null` / `undefined` / whitespace-only inputs — those
 * cases are "no prompt set", not "prompt ignored". A single source of truth
 * for this rule prevents drift between the three call sites; without it the
 * frontend hint could disagree with the backend gate on whitespace handling.
 */
export function isTranscriptPromptIgnored(
  transcriptModel: OpenAIRealtimeTranscriptModel,
  rawPrompt: string | null | undefined
): boolean {
  if (rawPrompt === null || rawPrompt === undefined) return false;
  if (rawPrompt.trim().length === 0) return false;
  return !OPENAI_TRANSCRIPT_MODEL_CAPABILITIES[transcriptModel].prompt;
}

/**
 * Resolve the transcript model that will actually be sent on the wire for
 * a given realtime model + per-deployment override.
 *
 * Mirrors the backend session-config gate verbatim:
 *   - If the realtime model does NOT support transcript-model overrides
 *     (`capabilities.transcriptModel === false`, i.e. v1.5), the override
 *     is IGNORED and `"whisper-1"` is forced. Storing a v2-only override
 *     on a v1.5 deployment is intentionally allowed (preserves the
 *     preference across model upgrades) but never reaches the wire.
 *   - Otherwise the explicit override wins, falling back to the model's
 *     `defaultTranscriptModel`.
 *
 * Shared with the frontend hook so the UI's `effectiveTranscriptModel`
 * matches what the API will actually receive — preventing the false-
 * positive "your prompt is being ignored" hint that would fire when a
 * stale v2 override is preserved on a v1.5 deployment.
 */
export function resolveEffectiveTranscriptModel(
  model: OpenAIRealtimeModel,
  override: OpenAIRealtimeTranscriptModel | null | undefined
): OpenAIRealtimeTranscriptModel {
  if (!model.capabilities.transcriptModel) return "whisper-1";
  return override ?? model.defaultTranscriptModel;
}

/**
 * Reasoning-effort knob for models that expose `reasoning.effort` in the
 * session payload. Mirrors OpenAI's Realtime API vocabulary verbatim so the
 * stored value flows to the wire without translation.
 *
 * `minimal` and `xhigh` are GA additions over the original Responses-API
 * three-tier scale: `minimal` is the latency-optimised mode OpenAI recommends
 * as the production default for voice agents, and `xhigh` extends the
 * deliberation envelope for high-complexity workflows where latency matters
 * less than answer quality.
 */
export const OPENAI_REALTIME_REASONING_EFFORTS = [
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh"
] as const;
export type OpenAIRealtimeReasoningEffort = (typeof OPENAI_REALTIME_REASONING_EFFORTS)[number];
export const OpenAIRealtimeReasoningEffortSchema = z.enum(OPENAI_REALTIME_REASONING_EFFORTS);

/**
 * Tool-choice strategies the Realtime API accepts for `session.tool_choice`.
 * Functional shapes (e.g. `{ type: "function", name }`) are intentionally
 * omitted — they require runtime knowledge of the agent's tool list, which
 * lives in the session-config builder, not in stored deployment settings.
 *
 * - `auto` (default): model decides whether to call a tool.
 * - `required`: model MUST call a tool every turn — useful for strict
 *   workflows but rejected by the API when no tools are configured, so
 *   the session-config builder downgrades to `auto` defensively.
 * - `none`: model is forbidden from calling tools — pure conversational mode.
 */
export const OPENAI_REALTIME_TOOL_CHOICES = ["auto", "required", "none"] as const;
export type OpenAIRealtimeToolChoice = (typeof OPENAI_REALTIME_TOOL_CHOICES)[number];
export const OpenAIRealtimeToolChoiceSchema = z.enum(OPENAI_REALTIME_TOOL_CHOICES);

/**
 * Allowed range for `audio.output.speed`. Mirrors the OpenAI API's documented
 * 0.25–1.5 envelope; values outside this range produce `invalid_request`.
 */
export const OPENAI_REALTIME_AUDIO_SPEED_MIN = 0.25;
export const OPENAI_REALTIME_AUDIO_SPEED_MAX = 1.5;

/**
 * Truncation strategies the Realtime API accepts for `session.truncation`.
 * Functional shapes (`{type:"auto", retention_ratio: number}`) are
 * intentionally not modelled — production telephony has no use case for
 * partial-history retention, and exposing the knob without a real workflow
 * behind it would be UI bloat.
 *
 * - `auto` (default): when the conversation exceeds the model's context
 *   window, OpenAI silently truncates the oldest items. Safest choice for
 *   long-running calls.
 * - `disabled`: the API returns an error when context overflows. Useful for
 *   strict workflow agents that would rather fail loudly than silently
 *   forget early caller context.
 */
export const OPENAI_REALTIME_TRUNCATION_STRATEGIES = ["auto", "disabled"] as const;
export type OpenAIRealtimeTruncationStrategy =
  (typeof OPENAI_REALTIME_TRUNCATION_STRATEGIES)[number];
export const OpenAIRealtimeTruncationStrategySchema = z.enum(OPENAI_REALTIME_TRUNCATION_STRATEGIES);

/** Voices OpenAI explicitly recommends for gpt-realtime-2 (release notes). */
const V2_RECOMMENDED_VOICES: readonly OpenAIRealtimeVoice[] = ["marin", "cedar"];

export const OPENAI_REALTIME_MODELS: Record<OpenAIRealtimeModelId, OpenAIRealtimeModel> = {
  // "GPT-Realtime-2.1 updates GPT-Realtime-2 with improved alphanumeric
  // recognition, silence and noise handling, and interruption behavior."
  // Same context window (128,000), same max output (32,000), same rates on
  // every modality, same `reasoning.effort` enum and the same ten voices with
  // marin/cedar recommended — so every field below is gpt-realtime-2's.
  // Model page: https://developers.openai.com/api/docs/models/gpt-realtime-2.1
  // Voices: https://developers.openai.com/api/docs/guides/realtime-conversations
  // The session.update this table produces for it was sent to the live API and
  // accepted with every capability on: the probe and its recordings sit in
  // apps/backend/src/voice-relay/infrastructure/adapters/openai/__fixtures__/.
  "gpt-realtime-2.1": {
    id: "gpt-realtime-2.1",
    displayName: "GPT Realtime 2.1",
    contextWindowTokens: 128_000,
    maxInstructionsChars: 60_000,
    temperature: 0.7,
    defaultVoice: "marin",
    voices: V2_VOICES,
    recommendedVoices: V2_RECOMMENDED_VOICES,
    defaultTranscriptModel: "gpt-realtime-whisper",
    pricing: {
      audioInput: 32,
      audioInputCached: 0.4,
      audioOutput: 64,
      textInput: 4,
      textInputCached: 0.4,
      textOutput: 24
    },
    capabilities: {
      preambles: true,
      reasoningEffort: true,
      maxResponseOutputTokens: true,
      transcriptModel: true,
      waitForUser: true,
      audioOutputSpeed: true,
      openAITracing: true
    }
  },
  // "GPT-Realtime-2.1 Mini is a distilled reasoning model for faster,
  // lower-cost realtime voice interactions" — same Realtime endpoint, 128,000
  // context window, 32,000 max output, function calling supported, reasoning
  // token support. Its rates are the one place it differs from gpt-realtime-2.1,
  // and its cached input is priced per modality: $0.30 audio against $0.06 text.
  // Model page: https://developers.openai.com/api/docs/models/gpt-realtime-2.1-mini
  // Pricing: https://developers.openai.com/api/docs/pricing
  // The session.update this table produces for it, with every capability on,
  // was sent to the live API and accepted, and the model spoke and called the
  // tool: the probe and its recordings sit in
  // apps/backend/src/voice-relay/infrastructure/adapters/openai/__fixtures__/.
  "gpt-realtime-2.1-mini": {
    id: "gpt-realtime-2.1-mini",
    displayName: "GPT Realtime 2.1 Mini",
    contextWindowTokens: 128_000,
    maxInstructionsChars: 60_000,
    temperature: 0.7,
    defaultVoice: "marin",
    voices: V2_VOICES,
    recommendedVoices: V2_RECOMMENDED_VOICES,
    defaultTranscriptModel: "gpt-realtime-whisper",
    pricing: {
      audioInput: 10,
      audioInputCached: 0.3,
      audioOutput: 20,
      textInput: 0.6,
      textInputCached: 0.06,
      textOutput: 2.4
    },
    capabilities: {
      preambles: true,
      reasoningEffort: true,
      maxResponseOutputTokens: true,
      transcriptModel: true,
      waitForUser: true,
      audioOutputSpeed: true,
      openAITracing: true
    }
  },
  "gpt-realtime-2": {
    id: "gpt-realtime-2",
    displayName: "GPT Realtime 2",
    contextWindowTokens: 128_000,
    // 128K context — leave generous room for instructions while still preserving
    // the bulk of the budget for the conversation itself.
    maxInstructionsChars: 60_000,
    temperature: 0.7,
    defaultVoice: "marin",
    voices: V2_VOICES,
    recommendedVoices: V2_RECOMMENDED_VOICES,
    defaultTranscriptModel: "gpt-realtime-whisper",
    pricing: {
      audioInput: 32,
      audioInputCached: 0.4,
      audioOutput: 64,
      textInput: 4,
      textInputCached: 0.4,
      textOutput: 24
    },
    capabilities: {
      preambles: true,
      reasoningEffort: true,
      maxResponseOutputTokens: true,
      transcriptModel: true,
      waitForUser: true,
      audioOutputSpeed: true,
      openAITracing: true
    }
  },
  "gpt-realtime-1.5": {
    id: "gpt-realtime-1.5",
    displayName: "GPT Realtime 1.5",
    contextWindowTokens: 32_000,
    maxInstructionsChars: 40_000,
    temperature: 0.7,
    defaultVoice: "alloy",
    voices: V1_5_VOICES,
    // OpenAI does not publish a "best quality" recommendation for v1.5 voices;
    // leaving this empty surfaces every voice as an equal-tier option in the UI.
    recommendedVoices: [],
    defaultTranscriptModel: "whisper-1",
    pricing: {
      audioInput: 32,
      audioInputCached: 0.4,
      audioOutput: 64,
      textInput: 4,
      textInputCached: 0.4,
      textOutput: 16
    },
    capabilities: {
      preambles: false,
      reasoningEffort: false,
      maxResponseOutputTokens: false,
      transcriptModel: false,
      waitForUser: false,
      audioOutputSpeed: false,
      openAITracing: false
    }
  }
};

/**
 * Narrows a free-form string to a model id this table actually carries.
 *
 * Membership is tested against the id ARRAY, never with `in` against the table.
 * `in` walks the prototype chain, so `constructor`, `__proto__` and the rest of
 * `Object.prototype` report as valid ids and index the table to a truthy
 * non-value — which then sails past the `??`/`if` that was meant to catch them.
 * `Array.prototype.includes` walks nothing.
 */
export function isOpenAIRealtimeModelId(value: string): value is OpenAIRealtimeModelId {
  return (OPENAI_REALTIME_MODEL_IDS as readonly string[]).includes(value);
}

/** Default model assigned to new deployments. */
export const DEFAULT_OPENAI_REALTIME_MODEL: OpenAIRealtimeModelId = "gpt-realtime-2";

/** Resolve a model entry, falling back to the default for unknown / undefined IDs. */
export function resolveOpenAIRealtimeModel(
  id: OpenAIRealtimeModelId | string | undefined
): OpenAIRealtimeModel {
  if (id !== undefined && isOpenAIRealtimeModelId(id)) {
    return OPENAI_REALTIME_MODELS[id];
  }
  return OPENAI_REALTIME_MODELS[DEFAULT_OPENAI_REALTIME_MODEL];
}

/** Build the Realtime WebSocket URL for a given model. */
export function buildRealtimeWsUrl(id: OpenAIRealtimeModelId): string {
  return `wss://api.openai.com/v1/realtime?model=${id}`;
}

// ============================================================
// v2-only opt-in settings
// ============================================================

/**
 * Per-deployment OpenAI-specific feature toggles.
 * Each field is silently ignored when the selected model lacks the matching
 * capability, so settings can be enabled ahead of a model upgrade without
 * breaking calls — and downgrading the model never throws.
 *
 * Numeric / enum fields use `nullable().optional()` rather than just
 * `.optional()` so explicit "use the model default" can be distinguished
 * from "field not yet migrated" in stored JSON. Treat both as "default".
 */
export const OpenAIRealtimeFeatureSettingsSchema = z.object({
  /** Emit short "let me check that" phrases before tool calls. v2+ only. */
  enablePreambles: z.boolean().optional(),
  /**
   * Inject the `wait_for_user` no-op tool so the model has a valid
   * non-speaking action when it hears silence, hold music, or background
   * noise — replacing the default behaviour of producing filler phrases.
   * Default: enabled on capable models. v2+ only.
   */
  enableWaitForUser: z.boolean().optional(),
  /**
   * Override the user-transcript model. When unset, falls back to the
   * model registry's `defaultTranscriptModel`. v2+ only — gated on
   * `capabilities.transcriptModel`.
   */
  transcriptModel: OpenAIRealtimeTranscriptModelSchema.nullable().optional(),
  /**
   * Cap the number of output tokens per response. `null` / unset means
   * "model default" (which is unlimited on Realtime). v2+ only — gated
   * on `capabilities.maxResponseOutputTokens`.
   */
  maxResponseOutputTokens: z.number().int().positive().nullable().optional(),
  /**
   * Reasoning effort sent as `reasoning.effort`. v2+ only — gated on
   * `capabilities.reasoningEffort`. Higher = more deliberative responses
   * at the cost of latency.
   */
  reasoningEffort: OpenAIRealtimeReasoningEffortSchema.nullable().optional(),
  /**
   * Custom-vocabulary hints sent to the transcription model as
   * `input_audio_transcription.prompt`. Plain text — names, brand terms,
   * jargon — biasing the transcript towards terms Whisper might otherwise
   * mishear.
   *
   * Gated by the transcript model's capability flag
   * (`OPENAI_TRANSCRIPT_MODEL_CAPABILITIES[transcriptModel].prompt`).
   * `whisper-1` accepts the field; `gpt-realtime-whisper` rejects it with
   * `invalid_value` and triggers the GA silent-drop failure mode. The
   * value is still stored on the deployment so swapping the transcript
   * model back to `whisper-1` (or a future capable model) restores the
   * hint without re-typing it.
   *
   * Capped at 1000 characters — well under the underlying transcript
   * model's 244-token soft limit while leaving room for sentence-shaped
   * hints rather than just word lists.
   */
  transcriptPrompt: z.string().max(1000).nullable().optional(),
  /**
   * Speech playback rate for the synthesised audio, sent as
   * `audio.output.speed`. Range 0.25–1.5 (1.0 = normal). v2+ only — gated
   * on `capabilities.audioOutputSpeed`. Use cases: accessibility (slower
   * for elderly users), pacing for content-dense agents (faster for
   * impatient power users).
   */
  audioOutputSpeed: z
    .number()
    .min(OPENAI_REALTIME_AUDIO_SPEED_MIN)
    .max(OPENAI_REALTIME_AUDIO_SPEED_MAX)
    .nullable()
    .optional(),
  /**
   * Tool-choice strategy. Defaults to `"auto"` (current behaviour).
   * `"required"` forces a tool call every turn — useful for strict
   * workflow agents — but the API rejects it when no tools are configured,
   * so the session-config builder downgrades to `"auto"` defensively when
   * `tools.length === 0`. `"none"` disables tool calling entirely.
   */
  toolChoice: OpenAIRealtimeToolChoiceSchema.optional(),
  /**
   * Opt the session into OpenAI's own tracing dashboard. Independent of
   * our LLMTracingService — both can run in parallel. Useful when filing
   * a support ticket with OpenAI: their engineers can pull up the trace
   * server-side instead of asking for client-side reproductions. v2+
   * only — gated on `capabilities.openAITracing`.
   */
  enableOpenAITracing: z.boolean().optional(),
  /**
   * Context-overflow strategy sent as `session.truncation`. `"auto"` is
   * the API default and the right choice for almost every call. `"disabled"`
   * makes the API throw on overflow instead of silently dropping the oldest
   * items — useful for strict workflow agents that would rather fail loudly
   * than lose early context. Not capability-gated; both Realtime models
   * accept the field.
   */
  truncation: OpenAIRealtimeTruncationStrategySchema.optional()
});
export type OpenAIRealtimeFeatureSettings = z.infer<typeof OpenAIRealtimeFeatureSettingsSchema>;

/**
 * Every field is set, and `satisfies` keeps each one's literal type, so a
 * reader resolving `stored ?? DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS.<field>`
 * gets a value of the field's type and needs no literal fallback of its own.
 */
export const DEFAULT_OPENAI_REALTIME_FEATURE_SETTINGS = {
  enablePreambles: true,
  enableWaitForUser: true,
  // null = inherit model default (`defaultTranscriptModel`).
  transcriptModel: null,
  // null = unlimited (OpenAI's own default).
  maxResponseOutputTokens: null,
  // "low" is OpenAI's recommended production default for voice agents —
  // higher tiers add latency that's audible on a phone call.
  reasoningEffort: "low",
  // null = no Whisper prompt bias.
  transcriptPrompt: null,
  // null = OpenAI's default playback rate (1.0×).
  audioOutputSpeed: null,
  // "auto" mirrors the previous hardcoded behaviour — every existing
  // deployment keeps working unchanged.
  toolChoice: "auto",
  // OpenAI-side tracing is opt-in for privacy: customers may not want
  // OpenAI keeping a copy of their call traces by default.
  enableOpenAITracing: false,
  // Auto matches the API default; deployments can opt into strict mode
  // when overflow handling matters more than uptime.
  truncation: "auto"
} as const satisfies Required<OpenAIRealtimeFeatureSettings>;
